import { useState } from 'react'
import { useFetchDeliveryLocationTypes } from '@/hooks/features/delivery-location-type'
import { OrderModel } from '@/http/models/Order/Order'
import { DeliveryLocationTypeModel as DeliveryLocationTypeApi } from '@/http/models/DeliveryLocationType'
import { toast } from 'sonner'
import { DeliveryLocationTypeModel } from '@/types'
import {
    DeliveryLocationTypeDialog,
    type DeliveryLocationTypeFormState,
} from '@/components/features/delivery-location-type'
import { SearchOrCreateCombobox } from './SearchOrCreateCombobox'

interface DeliveryLocationTypeSelectProps {
    orderId: string
    currentLocationTypeId: string | null
    /** The order's expanded delivery area name, for when it isn't in the first page of areas. */
    currentLabel?: string
    onUpdate?: () => void
}

export const DeliveryLocationTypeSelect = ({ orderId, currentLocationTypeId, currentLabel, onUpdate }: DeliveryLocationTypeSelectProps) => {
    const [loading, setLoading] = useState(false)
    const [created, setCreated] = useState<DeliveryLocationTypeModel.DeliveryLocationType | null>(null)
    // A new area needs a charge, so creating opens the add dialog with the typed name instead of saving straight away.
    const [createName, setCreateName] = useState<string | null>(null)
    const [createLoading, setCreateLoading] = useState(false)
    const { data: fetchedTypes, loading: loadingTypes } = useFetchDeliveryLocationTypes({
        shouldFetchList: true,
    })

    const locationTypes = created ? [created, ...fetchedTypes.filter(t => t._id !== created._id)] : fetchedTypes

    const handleUpdate = async (locationTypeId: string) => {
        setLoading(true)
        const response = await OrderModel.updateDeliveryLocationType({
            params: { id: orderId },
            body: { delivery_location_type_id: locationTypeId },
        })

        if (response.success) {
            toast.success('Delivery location type updated')
            onUpdate?.()
        } else {
            toast.error('Failed to update delivery location type')
        }
        setLoading(false)
    }

    const searchTypes = async (search: string) => {
        const response = await DeliveryLocationTypeApi.getList({ query: { search, page: 1, length: 10 } })
        return response.success ? (response.data?.result ?? []) : []
    }

    const handleCreate = async (data: DeliveryLocationTypeFormState) => {
        setCreateLoading(true)
        const response = await DeliveryLocationTypeApi.create({ body: data })
        const type: DeliveryLocationTypeModel.DeliveryLocationType | undefined = response.data?.result

        if (response.success && type?._id) {
            toast.success(`Delivery area "${data.name}" created`)
            setCreated(type)
            setCreateName(null)
            await handleUpdate(type._id)
        } else {
            toast.error(response.error?.data?.message || 'Failed to create delivery area')
        }
        setCreateLoading(false)
    }

    return (
        <>
            <SearchOrCreateCombobox
                items={locationTypes}
                currentId={currentLocationTypeId}
                currentLabel={currentLabel}
                getId={(type) => type._id}
                getLabel={(type) => type.name}
                renderDetail={(type) => `Charge: ${type.charge}`}
                search={searchTypes}
                onSelect={(type) => handleUpdate(type._id)}
                onCreate={setCreateName}
                placeholder="Select location type"
                searchPlaceholder="Search or create an area"
                emptyText="No location type found."
                busy={loading || loadingTypes}
            />
            <DeliveryLocationTypeDialog
                open={createName !== null}
                mode="create"
                editDeliveryLocationType={null}
                defaultName={createName ?? ''}
                closeDialog={() => setCreateName(null)}
                onCreate={handleCreate}
                onUpdate={async () => {}}
                loading={createLoading}
            />
        </>
    )
}
